import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form } from 'src/registry/ui/Form';
import { AccountingHeadingFormSchemaValues } from 'src/features/accountingHeaders/utils/formSchema';
import { commonMessages } from 'src/utils/messages';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { ACCOUNTING_HEADING_TYPE, AccountingHeading } from 'src/features/accountingHeaders/utils/types';
import { ACCOUNTING_HEADER_TYPE_TRANSLATION_MAP } from 'src/features/accountingHeaders/utils/constants';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { billingMessages } from 'src/features/billing/utils/messages';
import React from 'react';
import { useListBillingEntities } from 'src/features/billing/api/useListBillingEntities';

type AccountingHeaderFormProps = {
    form: UseFormReturn<AccountingHeadingFormSchemaValues>;
    onSubmit: (values: AccountingHeadingFormSchemaValues) => void;
    accountingHeaders?: AccountingHeading[];
    setSearchAccountingHeadings?: (search: string) => void;
};

function AccountingHeaderForm({
    onSubmit,
    form,
    accountingHeaders,
    setSearchAccountingHeadings,
}: AccountingHeaderFormProps) {
    const { formatMessage } = useIntl();

    const [searchBillingEntity, setSearchBillingEntity] = React.useState<string>('');

    const { data: billingEntities } = useListBillingEntities({
        search: searchBillingEntity,
    });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<AccountingHeadingFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<AccountingHeadingFormSchemaValues>
                form={form}
                name="account"
                label={formatMessage(accountingHeadersMessages.account)}
            />
            <SelectFormField<AccountingHeadingFormSchemaValues>
                form={form}
                name="type"
                label={formatMessage(commonMessages.type)}
                options={Object.values(ACCOUNTING_HEADING_TYPE)}
                optionTranslationMap={ACCOUNTING_HEADER_TYPE_TRANSLATION_MAP}
            />
            <SearchSelectFormField
                name="billing_entity"
                control={form.control}
                label={formatMessage(billingMessages.billingEntity)}
                items={billingEntities?.results ?? []}
                clearable
                placeholder={`${formatMessage(billingMessages.billingEntity)}...`}
                searchPlaceholder={formatMessage(billingMessages.searchBillingEntity)}
                emptyTitle={formatMessage(billingMessages.noBillingEntity)}
                emptyDescription={formatMessage(billingMessages.noBillingEntityFound)}
                getItemValue={(entity) => entity.id}
                getItemLabel={(entity) => entity.name}
                getItemKeywords={(entity) => [entity.name]}
                loadOptions={setSearchBillingEntity}
            />
            <InputFormField<AccountingHeadingFormSchemaValues>
                form={form}
                name="partide"
                required={false}
                label={formatMessage(accountingHeadersMessages.partide)}
            />

            <SearchSelectFormField
                name="parent"
                control={form.control}
                label={formatMessage(accountingHeadersMessages.parent)}
                items={accountingHeaders ?? []}
                clearable
                placeholder={`${formatMessage(accountingHeadersMessages.parent)}...`}
                searchPlaceholder={formatMessage(accountingHeadersMessages.searchParent)}
                emptyTitle={formatMessage(accountingHeadersMessages.noAccountingHeader)}
                emptyDescription={formatMessage(accountingHeadersMessages.noAccountingHeaderFound)}
                getItemValue={(header) => header.id}
                getItemLabel={(header) => header.name}
                getItemKeywords={(header) => [header.name]}
                loadOptions={setSearchAccountingHeadings}
                required={false}
            />
        </Form>
    );
}

export default AccountingHeaderForm;
